import { Button } from "@mui/joy";
import { useEffect, useState } from "react";
import {
  getMultiPlanPrices,
  getSinglePlanPrice,
  PlanKey,
  planKeyToName,
} from "../../components/task/payment/planTypes";
import {
  loadTossPayments,
  TossPaymentsPayment,
} from "@tosspayments/tosspayments-sdk";
import { v4 as uuidv4 } from "uuid";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import { trackXAdsEvent } from "analytics/xAdsPixel";
import useMixpanelMKT from "../../hooks/useMixpanelMKT";
import { userSimpleAtom } from "../../recoil/userSimpleAtom";
import { useRecoilValue } from "recoil";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import { useNavigate } from "react-router-dom";
import { getFunnelPaths } from "routes/adFunnel";
import useGtagConversion from "../../hooks/useGtagConversion";
import { useMutation } from "@tanstack/react-query";
import { webFunnelApi } from "../../api/webFunnelApi";

export default function PaymentButtonPromotion({
  selectedPlan,
}: {
  selectedPlan: PlanKey;
}) {
  const tossPaymentsClientKey = process.env.REACT_APP_TOSSPAYMENTS_CLIENT_KEY!!;

  const user = useRecoilValue(userSimpleAtom);
  const { isChecking } = useAutoLoginPromotion();
  const navigate = useNavigate();
  const [tossPaymentsPayment, setTossPaymentsPayment] =
    useState<TossPaymentsPayment | null>();

  const trackGtagEvent = useGtagConversion();

  const { mutate: trackEvent } = useMutation(
    ({
      eventName,
      properties,
    }: {
      eventName: string;
      properties?: { [key: string]: any };
    }) => webFunnelApi.trackEvent(eventName, properties)
  );

  const trackPromotion = useMixpanelMKT({
    eventName: "결제하기 버튼 클릭",
    version: "v4",
  });

  const buttonText = "결제하기";

  function computeAmountValue(): number {
    const pricingModel = user?.pricingModel;

    // e/h/i: 단일 "맞춤 플랜"이 challenger로 매핑되며 모델별 가격 오버라이드
    const singlePlanPrice = getSinglePlanPrice(pricingModel);
    if (singlePlanPrice !== undefined && selectedPlan === "challenger") {
      return singlePlanPrice;
    }

    // aeg: 다중 플랜 전체 가격 오버라이드
    const multiPlanPrices = getMultiPlanPrices(pricingModel);
    if (multiPlanPrices) {
      return multiPlanPrices[selectedPlan] ?? multiPlanPrices.challenger;
    }

    // 기본 가격
    if (selectedPlan === "starter") return 295000;
    if (selectedPlan === "challenger") return 395000;
    if (selectedPlan === "master") return 465000;
    return 395000;
  }

  async function requestTossPayments() {
    if (tossPaymentsPayment) {
      try {
        // PaymentFailPromotion 에서 Toss 도메인 항목을 정확히 건너뛰기 위해
        // 결제 시작 직전 history.length 를 저장한다.
        try {
          sessionStorage.setItem(
            "promotionPaymentStartHistoryLength",
            String(window.history.length)
          );
        } catch {}

        await tossPaymentsPayment?.requestPayment({
          method: "CARD",
          amount: {
            currency: "KRW",
            value: computeAmountValue(),
          },
          orderId: uuidv4(),
          orderName: `디스턴싱 ${planKeyToName[selectedPlan]} 플랜 / 100일`,
          customerEmail: user?.email || "",
          customerName: user?.nickname,
          // 결제 후 복귀 URL 은 현재 퍼널 prefix(/promotion, /gads)를 따른다
          successUrl: window.location.origin + getFunnelPaths().tossSuccess,
          failUrl: window.location.origin + getFunnelPaths().tossFail,
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      } catch (error) {
        // 에러 처리하기
        console.error(error);
      }
    }
  }

  useEffect(() => {
    async function fetchTossPaymentsPayment() {
      if (tossPaymentsClientKey) {
        const tossPayments = await loadTossPayments(tossPaymentsClientKey);
        const payment = tossPayments.payment({
          customerKey: `ck_${uuidv4()}`,
        });

        setTossPaymentsPayment(payment);
      }
    }

    fetchTossPaymentsPayment();
  }, [tossPaymentsClientKey]);

  // 로그인 체크 후 리다이렉트
  useEffect(() => {
    if (!isChecking && !user?.accessToken) {
      navigate(getFunnelPaths().login, { replace: true });
    }
  }, [isChecking, user, navigate]);

  const handlePaymentRequest = () => {
    trackCapiStandardEvent("InitiateCheckout", {
      externalId: user?.userId,
      email: user?.email,
    });
    trackGtagEvent("InitiateCheckout", "v4", {
      email: user?.email,
    });
    trackXAdsEvent("AddPaymentInfo", {
      email: user?.email,
    });
    trackPromotion();

    trackEvent({
      eventName: "결제 버튼 클릭",
      properties: {
        event_source: "web",
      },
    });

    requestTossPayments();
  };

  return (
    <>
      <Button
        loading={isChecking}
        disabled={
          isChecking || !user?.accessToken || user?.isSubscribing === true
        }
        onClick={() => {
          handlePaymentRequest();
        }}
        size={"lg"}
        color={"primary"}
        sx={{
          width: "100%",
          fontWeight: 700,
        }}
      >
        {buttonText}
      </Button>
    </>
  );
}
